export interface DesignOntologyRelease {
  schemaVersion: "cogo.design-ontology-release.v1"
  releaseId: string
  immutable: true
  presentation: DesignOntologyPresentation
  roles: Record<string, string>
  contentHash: string
}

export interface DesignOntologyPresentation {
  colorSchemes: {
    light: Record<string, string>
    dark: Record<string, string>
  }
  radius: string
  spacing: string
  typography: {
    sans: string
    heading: string
  }
}

export interface StructuralFingerprintOptions {
  eventIdentityByElementId?: Readonly<Record<string, readonly string[]>>
  nodeIdentity?: (node: Element) => string
}

export interface DesignOntologyReleaseEpoch {
  releaseId: string
  epoch: number
  instances: string[]
}

export interface DesignOntologyVisualProbeSnapshot {
  releaseId: string
  instances: Array<Record<string, string>>
}

export interface DesignOntologyStructuralFingerprintSwapPair {
  fromReleaseId: string
  toReleaseId: string
  before: string[]
  after: string[]
}

export interface DesignOntologyLiveSwapEvaluation {
  catalogRoles: string[]
  releases: Record<string, DesignOntologyRelease>
  referencedHandles: string[]
  unresolvedHandles: string[]
  blockArtifactHashes: string[]
  structuralFingerprints: string[]
  structuralFingerprintSwapPairs: DesignOntologyStructuralFingerprintSwapPair[]
  interactionStates: unknown[]
  focusedElementIds: Array<string | null>
  focusContractuallyRestored: boolean
  actionFlowTraces: unknown[]
  actionFlowTraceDeltas: unknown[]
  declaredEventIdentities: string[]
  mountIds: string[][]
  rendererNodeIdentitySnapshots: string[][]
  releaseEpochs: DesignOntologyReleaseEpoch[]
  visualProbeSnapshots: DesignOntologyVisualProbeSnapshot[]
  rawVisualClassCount: number
  inlineVisualStyleCount: number
  unknownElementCount: number
  consoleErrorCount: number
  swapLatencyMs: number
  sourceParity: {
    independentReference: boolean
    referenceContentHash: string
    differentPixels: number
    totalPixels: number
  }
}

export interface DesignOntologyLiveSwapReceipt {
  schemaVersion: "cogo.design-ontology-live-swap-receipt.v1"
  designAuthorityCoverage: number
  rawVisualClassCount: number
  inlineVisualStyleCount: number
  unknownElementCount: number
  consoleErrorCount: number
  swapLatencyMs: number
  ontologyReleaseSwapPass: boolean
  blockArtifactHashUnchanged: boolean
  structuralFingerprintUnchanged: boolean
  interactionStatePreserved: boolean
  focusPreservedOrContractuallyRestored: boolean
  actionFlowTraceUnchanged: boolean
  declaredEventCoverageComplete: boolean
  rendererNodeIdentityUnchanged: boolean
  sourceParityMatched: boolean
  unresolvedDesignAuthorityCount: number
  blockers: string[]
}

export const DESIGN_HANDLE_PATTERN = /^@ds:[A-Za-z0-9._:-]+$/

/**
 * Attributes excluded by the pre-existing style-decoupling oracle. The live
 * swap fingerprint is stricter by construction: it records a semantic
 * allow-list only, so none of these style/runtime attributes enter the hash.
 */
export const STRUCTURAL_FINGERPRINT_IGNORED_ATTRIBUTES = Object.freeze([
  "style",
  "class",
  "data-cogo-element-id",
  "data-testid",
  "data-atom-type",
  "data-variant",
  "data-size",
  "data-action",
  "id",
  "for",
  "aria-describedby",
  "aria-labelledby",
] as const)

interface StructuralNodeFact {
  order: number
  elementId: string
  nodeIdentity: string
  parentNodeIdentity: string | null
  childNodeIdentities: string[]
  semanticRole: string
  accessibleName: string
  cogoType: string
  eventIdentity: string[]
}

/**
 * Hashes only semantic structure. Classes, inline/computed style, generated
 * ids, and the bounded style-oracle ignore list never affect the result.
 */
export function structuralFingerprint(
  root: ParentNode,
  options: StructuralFingerprintOptions = {},
): string {
  const candidates: Element[] = []
  if (isElement(root) && root.matches("[data-cogo-element-id][data-cogo-type]")) {
    candidates.push(root)
  }
  candidates.push(
    ...Array.from(
      root.querySelectorAll("[data-cogo-element-id][data-cogo-type]"),
    ),
  )

  const candidateSet = new Set(candidates)
  const nodeIdentityByNode = new Map(
    candidates.map((node) => [
      node,
      options.nodeIdentity?.(node) ??
        `logical:${node.getAttribute("data-cogo-element-id") ?? ""}`,
    ]),
  )

  const facts: StructuralNodeFact[] = candidates.map((node, order) => {
    const elementId = node.getAttribute("data-cogo-element-id") ?? ""
    const semanticTarget = findSemanticTarget(node, elementId)
    const parent = closestStructuralParent(node, candidateSet)
    const children = candidates.filter(
      (candidate) => closestStructuralParent(candidate, candidateSet) === node,
    )
    return {
      order,
      elementId,
      nodeIdentity: nodeIdentityByNode.get(node) ?? "",
      parentNodeIdentity: parent
        ? (nodeIdentityByNode.get(parent) ?? null)
        : null,
      childNodeIdentities: children.map(
        (child) => nodeIdentityByNode.get(child) ?? "",
      ),
      semanticRole: semanticTarget ? implicitRole(semanticTarget) : "",
      accessibleName: semanticTarget
        ? accessibleName(semanticTarget)
        : normalizeText(node.textContent),
      cogoType: node.getAttribute("data-cogo-type") ?? "",
      eventIdentity: [
        ...(options.eventIdentityByElementId?.[elementId] ?? []),
      ].sort(),
    }
  })

  return `fnv1a64:${fnv1a64(stableSerialize(facts))}`
}

export function evaluateDesignOntologyLiveSwap(
  input: DesignOntologyLiveSwapEvaluation,
): DesignOntologyLiveSwapReceipt {
  const blockers: string[] = []
  const catalogRoles = [...new Set(input.catalogRoles)].sort()
  const releaseEntries = Object.entries(input.releases).sort(([left], [right]) =>
    left.localeCompare(right),
  )

  let resolvedRoleCount = 0
  let missingRoleCount = 0
  let invalidReleaseCount = 0
  for (const [, release] of releaseEntries) {
    if (
      release.schemaVersion !== "cogo.design-ontology-release.v1" ||
      release.immutable !== true ||
      !release.releaseId.trim() ||
      !release.contentHash.startsWith("sha256:")
    ) {
      invalidReleaseCount += 1
    }
    for (const role of catalogRoles) {
      const value = release.roles?.[role]
      if (typeof value === "string" && value.trim()) resolvedRoleCount += 1
      else missingRoleCount += 1
    }
  }

  if (missingRoleCount > 0) blockers.push("design-role-missing")
  if (invalidReleaseCount > 0) blockers.push("design-release-invalid")

  const malformedHandles = unique(
    input.referencedHandles.filter((handle) => !DESIGN_HANDLE_PATTERN.test(handle)),
  )
  const unresolvedHandles = unique(input.unresolvedHandles)
  if (malformedHandles.length > 0) blockers.push("design-handle-malformed")
  if (unresolvedHandles.length > 0) blockers.push("design-authority-unresolved")

  const blockArtifactHashUnchanged = stableAcross(input.blockArtifactHashes)
  if (!blockArtifactHashUnchanged) blockers.push("block-artifact-hash-changed")

  const structuralFingerprintUnchanged =
    input.structuralFingerprintSwapPairs.length >= 2 &&
    input.structuralFingerprintSwapPairs.every(
      (pair) =>
        pair.fromReleaseId.trim().length > 0 &&
        pair.toReleaseId.trim().length > 0 &&
        pair.fromReleaseId !== pair.toReleaseId &&
        pair.before.length === 2 &&
        pair.after.length === 2 &&
        stableSerialize(pair.before) === stableSerialize(pair.after),
    )
  if (!structuralFingerprintUnchanged) {
    blockers.push("structural-fingerprint-changed")
  }

  const interactionStatePreserved = stableAcrossSerialized(
    input.interactionStates,
  )
  if (!interactionStatePreserved) blockers.push("interaction-state-reset")

  const sameFocus =
    input.focusedElementIds.length >= 2 &&
    input.focusedElementIds[0] != null &&
    stableAcross(input.focusedElementIds)
  const focusPreservedOrContractuallyRestored =
    sameFocus || input.focusContractuallyRestored
  if (!focusPreservedOrContractuallyRestored) blockers.push("focus-not-preserved")

  const actionFlowTraceUnchanged = stableAcrossSerialized(input.actionFlowTraces)
  const actionFlowTraceDeltasUnchanged = stableAcrossSerialized(
    input.actionFlowTraceDeltas,
  )
  const declaredEventIdentities = unique(input.declaredEventIdentities)
  const declaredEventCoverageComplete =
    declaredEventIdentities.length > 0 &&
    input.actionFlowTraceDeltas.length >= 3 &&
    input.actionFlowTraceDeltas.every((delta) => {
      const observed = new Set(traceEventIdentities(delta))
      return declaredEventIdentities.every((identity) => observed.has(identity))
    })
  const actionFlowTraceContractHeld =
    actionFlowTraceUnchanged &&
    actionFlowTraceDeltasUnchanged &&
    declaredEventCoverageComplete
  if (!actionFlowTraceContractHeld) blockers.push("action-flow-trace-changed")

  const twoStableMounts =
    input.mountIds.length >= 2 &&
    input.mountIds.every(
      (ids) => ids.length === 2 && new Set(ids).size === 2,
    ) &&
    stableAcrossSerialized(input.mountIds)
  const rendererNodeIdentityUnchanged =
    input.rendererNodeIdentitySnapshots.length >= 3 &&
    input.rendererNodeIdentitySnapshots.every(
      (identities) =>
        identities.length === 2 &&
        identities.every((identity) => identity.trim().length > 0),
    ) &&
    stableAcrossSerialized(input.rendererNodeIdentitySnapshots)
  if (!twoStableMounts || !rendererNodeIdentityUnchanged) {
    blockers.push("renderer-remounted")
  }

  const releaseContextStayedAtomic =
    input.releaseEpochs.length >= 3 &&
    input.releaseEpochs.every(
      (entry) =>
        entry.instances.length === 2 &&
        entry.instances.every(
          (value) => value === `${entry.releaseId}@${entry.epoch}`,
        ),
    )
  if (!releaseContextStayedAtomic) blockers.push("release-context-diverged")

  const releaseVisualsDiffer =
    input.visualProbeSnapshots.length >= 3 &&
    input.visualProbeSnapshots.every(
      (snapshot) =>
        snapshot.instances.length === 2 &&
        stableAcrossSerialized(snapshot.instances),
    ) &&
    input.visualProbeSnapshots.slice(1).every((snapshot, index) => {
      const previous = input.visualProbeSnapshots[index]
      return (
        stableSerialize(previous.instances[0]) !==
        stableSerialize(snapshot.instances[0])
      )
    })
  if (!releaseVisualsDiffer) blockers.push("ontology-release-visual-noop")

  if (input.rawVisualClassCount > 0) blockers.push("raw-visual-class-present")
  if (input.inlineVisualStyleCount > 0) blockers.push("inline-visual-style-present")
  if (input.unknownElementCount > 0) blockers.push("unknown-element-rendered")
  if (input.consoleErrorCount > 0) blockers.push("browser-console-error")

  const sourceParityReferenceValid =
    input.sourceParity.independentReference === true &&
    /^sha256:[0-9a-f]{64}$/.test(input.sourceParity.referenceContentHash) &&
    Number.isInteger(input.sourceParity.totalPixels) &&
    input.sourceParity.totalPixels > 0 &&
    Number.isInteger(input.sourceParity.differentPixels) &&
    input.sourceParity.differentPixels >= 0
  if (!sourceParityReferenceValid) {
    blockers.push("source-parity-reference-invalid")
  }
  const sourceParityMatched =
    sourceParityReferenceValid && input.sourceParity.differentPixels === 0
  if (sourceParityReferenceValid && !sourceParityMatched) {
    blockers.push("source-parity-pixel-diff")
  }

  const expectedRoleCount = catalogRoles.length * releaseEntries.length
  const designAuthorityCoverage =
    expectedRoleCount === 0
      ? 0
      : Number((resolvedRoleCount / expectedRoleCount).toFixed(6))
  const unresolvedDesignAuthorityCount =
    missingRoleCount + malformedHandles.length + unresolvedHandles.length
  const stableBlockers = unique(blockers)

  return {
    schemaVersion: "cogo.design-ontology-live-swap-receipt.v1",
    designAuthorityCoverage,
    rawVisualClassCount: input.rawVisualClassCount,
    inlineVisualStyleCount: input.inlineVisualStyleCount,
    unknownElementCount: input.unknownElementCount,
    consoleErrorCount: input.consoleErrorCount,
    swapLatencyMs: finiteNonNegative(input.swapLatencyMs),
    ontologyReleaseSwapPass: stableBlockers.length === 0,
    blockArtifactHashUnchanged,
    structuralFingerprintUnchanged,
    interactionStatePreserved,
    focusPreservedOrContractuallyRestored,
    actionFlowTraceUnchanged: actionFlowTraceContractHeld,
    declaredEventCoverageComplete,
    rendererNodeIdentityUnchanged,
    sourceParityMatched,
    unresolvedDesignAuthorityCount,
    blockers: stableBlockers,
  }
}

export interface AuthoredDesignInputScan {
  referencedHandles: string[]
  unresolvedHandles: string[]
  rawVisualClassCount: number
  inlineVisualStyleCount: number
}

export function scanAuthoredBlockDesignInputs(
  block: unknown,
  releases: Readonly<Record<string, DesignOntologyRelease>>,
): AuthoredDesignInputScan {
  return scanAuthoredDesignInputs(block, releases)
}

export function scanAuthoredDesignInputs(
  root: unknown,
  releases: Readonly<Record<string, DesignOntologyRelease>>,
): AuthoredDesignInputScan {
  const referencedHandles: string[] = []
  const unresolvedHandles: string[] = []
  let rawVisualClassCount = 0
  let inlineVisualStyleCount = 0

  const scanClassValue = (value: string): void => {
    for (const token of value.split(/\s+/).filter(Boolean)) {
      if (token.startsWith("@ds:")) {
        referencedHandles.push(token)
        if (DESIGN_HANDLE_PATTERN.test(token)) {
          const role = token.slice("@ds:".length)
          for (const release of Object.values(releases)) {
            const resolved = release.roles?.[role]
            if (typeof resolved !== "string" || !resolved.trim()) {
              unresolvedHandles.push(`${release.releaseId}:${token}`)
            }
          }
        }
      } else {
        rawVisualClassCount += 1
      }
    }
  }

  const visit = (value: unknown): void => {
    if (!isRecord(value)) return
    if (typeof value.className === "string") {
      scanClassValue(value.className)
    }
    if (isRecord(value.classNameVariants)) {
      for (const branch of Object.values(value.classNameVariants)) {
        if (typeof branch === "string") scanClassValue(branch)
      }
    }
    if (isRecord(value.style)) {
      inlineVisualStyleCount += Object.keys(value.style).length
    }
    if (Array.isArray(value.children)) {
      for (const child of value.children) visit(child)
    }
    if (isRecord(value.definitions)) {
      for (const definition of Object.values(value.definitions)) visit(definition)
    }
    if (isRecord(value.root)) visit(value.root)
  }

  visit(root)
  return {
    referencedHandles: unique(referencedHandles),
    unresolvedHandles: unique(unresolvedHandles),
    rawVisualClassCount,
    inlineVisualStyleCount,
  }
}

export interface DesignRoleCatalogValidation {
  valid: boolean
  errors: string[]
  roles: string[]
  familyNamespaces: string[]
  styleScopes: string[]
}

export function validateDesignRoleCatalog(
  catalog: unknown,
  canonicalNamespaces: readonly string[],
  expectedStyleScopes: readonly string[],
): DesignRoleCatalogValidation {
  const errors: string[] = []
  const record = isRecord(catalog) ? catalog : {}
  if (record.schemaVersion !== "cogo.design-role-catalog.v1") {
    errors.push("catalog-schema-version-invalid")
  }
  if (
    typeof record.catalogId !== "string" ||
    !/^[a-z][a-z0-9-]*(?:\.[a-z][a-z0-9-]*)*\.v[1-9][0-9]*$/.test(
      record.catalogId,
    )
  ) {
    errors.push("catalog-id-invalid")
  }

  const familyNamespaces = stringArray(record.familyNamespaces)
  const canonical = unique(canonicalNamespaces)
  if (
    !Array.isArray(record.familyNamespaces) ||
    record.familyNamespaces.length !== familyNamespaces.length ||
    familyNamespaces.length !== new Set(familyNamespaces).size ||
    stableSerialize([...familyNamespaces].sort()) !==
      stableSerialize([...canonical].sort())
  ) {
    errors.push("catalog-family-namespaces-invalid")
  }

  const styleScopes = stringArray(record.styleScopes)
  if (
    !Array.isArray(record.styleScopes) ||
    record.styleScopes.length !== styleScopes.length ||
    stableSerialize(styleScopes) !== stableSerialize(expectedStyleScopes)
  ) {
    errors.push("catalog-style-scopes-invalid")
  }

  const roleEntries = Array.isArray(record.roles) ? record.roles : []
  if (!Array.isArray(record.roles)) errors.push("catalog-roles-invalid")
  const roles: string[] = []
  const validEntries: Array<{
    familyNamespace: string
    role: string
    kind: string
    required: boolean
  }> = []

  roleEntries.forEach((value, index) => {
    if (!isRecord(value)) {
      errors.push(`catalog-role-entry-invalid:${index}`)
      return
    }
    const familyNamespace =
      typeof value.familyNamespace === "string" ? value.familyNamespace : ""
    const role = typeof value.role === "string" ? value.role : ""
    const kind = typeof value.kind === "string" ? value.kind : ""
    const required = value.required
    if (!canonical.includes(familyNamespace)) {
      errors.push(`catalog-role-namespace-invalid:${role || index}`)
    }
    if (!/^[a-z][A-Za-z0-9]*$/.test(kind)) {
      errors.push(`catalog-role-kind-invalid:${role || index}`)
    }
    if (typeof required !== "boolean") {
      errors.push(`catalog-role-required-invalid:${role || index}`)
    }
    if (role !== `uiBlock.${familyNamespace}.${kind}`) {
      errors.push(`catalog-role-identity-invalid:${role || index}`)
    }
    if (role) roles.push(role)
    if (
      canonical.includes(familyNamespace) &&
      /^[a-z][A-Za-z0-9]*$/.test(kind) &&
      typeof required === "boolean" &&
      role === `uiBlock.${familyNamespace}.${kind}`
    ) {
      validEntries.push({ familyNamespace, role, kind, required })
    }
  })

  if (roles.length !== new Set(roles).size) {
    errors.push("catalog-role-duplicate")
  }
  for (const familyNamespace of canonical) {
    if (
      !validEntries.some(
        (entry) =>
          entry.familyNamespace === familyNamespace &&
          entry.role === `uiBlock.${familyNamespace}.root` &&
          entry.kind === "root" &&
          entry.required === true,
      )
    ) {
      errors.push(`catalog-root-role-metadata-invalid:${familyNamespace}`)
    }
  }

  return {
    valid: errors.length === 0,
    errors: unique(errors),
    roles,
    familyNamespaces,
    styleScopes,
  }
}

export function stableSerialize(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "undefined"
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(",")}]`
  }
  const record = value as Record<string, unknown>
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableSerialize(record[key])}`)
    .join(",")}}`
}

function stableAcross<T>(values: readonly T[]): boolean {
  return values.length >= 2 && values.every((value) => Object.is(value, values[0]))
}

function stableAcrossSerialized(values: readonly unknown[]): boolean {
  return (
    values.length >= 2 &&
    values.every(
      (value) => stableSerialize(value) === stableSerialize(values[0]),
    )
  )
}

function traceEventIdentities(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    if (!isRecord(entry)) return []
    const instanceId =
      typeof entry.instanceId === "string" ? entry.instanceId : ""
    const elementId = typeof entry.elementId === "string" ? entry.elementId : ""
    const eventType = typeof entry.eventType === "string" ? entry.eventType : ""
    const flowId = typeof entry.flowId === "string" ? entry.flowId : ""
    if (!elementId || !eventType || !flowId) return []
    return [
      `${instanceId ? `${instanceId}:` : ""}${elementId}:${eventType}:${flowId}`,
    ]
  })
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values)]
}

function finiteNonNegative(value: number): number {
  return Number.isFinite(value) && value >= 0 ? value : 0
}

function isElement(value: unknown): value is Element {
  return (
    value != null &&
    typeof value === "object" &&
    "matches" in value &&
    typeof (value as Element).matches === "function"
  )
}

function findSemanticTarget(node: Element, elementId: string): Element | null {
  if (implicitRole(node)) return node
  const queue = Array.from(node.children)
  while (queue.length > 0) {
    const candidate = queue.shift()!
    const nestedId = candidate.getAttribute("data-cogo-element-id")
    if (nestedId && nestedId !== elementId) continue
    if (implicitRole(candidate)) return candidate
    queue.push(...Array.from(candidate.children))
  }
  return node
}

function closestStructuralParent(
  node: Element,
  candidates: ReadonlySet<Element>,
): Element | null {
  let parent = node.parentElement
  while (parent) {
    if (candidates.has(parent)) return parent
    parent = parent.parentElement
  }
  return null
}

function implicitRole(element: Element): string {
  const explicit = element.getAttribute("role")
  if (explicit) return explicit
  const tag = element.tagName.toLowerCase()
  if (tag === "button") return "button"
  if (tag === "a" && element.hasAttribute("href")) return "link"
  if (tag === "textarea") return "textbox"
  if (tag === "select") return element.hasAttribute("multiple") ? "listbox" : "combobox"
  if (tag === "img") return "img"
  if (tag === "table") return "table"
  if (tag === "ul" || tag === "ol") return "list"
  if (tag === "li") return "listitem"
  if (tag === "input") {
    const type = (element.getAttribute("type") ?? "text").toLowerCase()
    if (type === "hidden") return ""
    if (type === "checkbox") return "checkbox"
    if (type === "radio") return "radio"
    if (type === "range") return "slider"
    if (type === "search") return "searchbox"
    return "textbox"
  }
  return ""
}

function accessibleName(element: Element): string {
  const ariaLabel = element.getAttribute("aria-label")
  if (ariaLabel) return normalizeText(ariaLabel)
  const labelledBy = element.getAttribute("aria-labelledby")
  if (labelledBy) {
    const text = labelledBy
      .split(/\s+/)
      .map((id) => element.ownerDocument.getElementById(id)?.textContent ?? "")
      .join(" ")
    if (normalizeText(text)) return normalizeText(text)
  }
  const alt = element.getAttribute("alt")
  if (alt) return normalizeText(alt)
  const title = element.getAttribute("title")
  if (title) return normalizeText(title)
  return normalizeText(element.textContent)
}

function normalizeText(value: string | null): string {
  return (value ?? "").replace(/\s+/g, " ").trim()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value)
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : []
}

function fnv1a64(value: string): string {
  let hash = 0xcbf29ce484222325n
  const prime = 0x100000001b3n
  for (const byte of new TextEncoder().encode(value)) {
    hash ^= BigInt(byte)
    hash = BigInt.asUintN(64, hash * prime)
  }
  return hash.toString(16).padStart(16, "0")
}
